import { useQuery } from '@tanstack/react-query';

import type { GetLeadStatsResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { leadsEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get lead stats
 * @param servicesProviderId - Required services provider ID
 */
export const getLeadStats = async (servicesProviderId: string): Promise<GetLeadStatsResponse> => {
	const response = await apiClient.get<GetLeadStatsResponse>(
		leadsEndpoints.stats(servicesProviderId),
	);
	return response.data;
};

/**
 * Hook for fetching lead statistics
 * @returns Query result with aggregate stats (total value, weighted value, open/closed, avg age)
 * @example
 * ```tsx
 * const { data: stats } = useGetLeadStats();
 * console.log(`Total value: ${stats?.totalLeadValue}`);
 * ```
 */
export const useGetLeadStats = () => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.leads.stats(servicesProviderId).queryKey,
		queryFn: () => getLeadStats(servicesProviderId),
		enabled: Boolean(servicesProviderId),
		// Stats can be cached briefly (30s) since they're aggregate data
		// More frequent updates aren't necessary and reduce server load
		// Still fresh enough for dashboard display
		staleTime: 30_000,
		...errorBoundaryOptions,
	});
};
